import React, { useRef, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, xcss, Inline } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';

export type CommentAddPromptProps = {
	onFocus: () => void; // For both clicking into the box and focusing the box via the tab key
	// TODO - COMMENTS-4934: Clean up the following comment when confluence_frontend_comments_a11y_kb FG is removed
	onClick: () => void; // FG ON: For clicking into the box when the box is already focused or FG OFF: For clicking into the box
	onBlur: () => void; // For when the box - or its children - loses focus
	autofocusRef: { current: boolean | null };
	ariaLabel?: string;
};

const commentAddPromptContainerStyle = xcss({
	font: 'inherit',
	width: '100%',
	height: '40px',
	color: 'color.text.subtlest',
	display: 'inline-flex',
	verticalAlign: 'top',
	marginLeft: 'space.100',
	flexGrow: 1,
	backgroundColor: 'elevation.surface.overlay',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.100',
	paddingLeft: 'space.100',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		cursor: 'text',
	},
});

const i18n = defineMessages({
	commentPanelFooterPlaceholder: {
		id: 'comments-panel.comments.add.prompt-placeholder',
		defaultMessage: 'Add text, @mention others, or use / for elements',
		description: 'Placeholder on the comment panel footer elememt to add a comment',
	},
});

const commentAddPromptMessage = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	color: 'color.text.subtlest',
});

export const CommentAddPrompt: React.FC<CommentAddPromptProps> = ({
	onFocus,
	onClick,
	onBlur,
	autofocusRef,
	ariaLabel,
}: CommentAddPromptProps) => {
	const buttonRef = useRef<HTMLButtonElement>(null);

	const { formatMessage } = useIntl();

	useEffect(() => {
		if (autofocusRef.current) {
			buttonRef.current?.focus();
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	const handleKeydown = useCallback(
		(event: React.KeyboardEvent) => {
			// Simulate a click on the element when the user presses Enter or Space or
			// Ctrl-Option-Space (which the a11y page reader says will activate the element)
			if (
				event.key === 'Enter' ||
				event.key === ' ' ||
				(event.key === ' ' && event.ctrlKey && event.altKey)
			) {
				event.preventDefault();
				onClick();
			}
		},
		[onClick],
	);

	// handleFocus() will open the general comment entry when either the user clicks in the element or tabs to it.
	// Note that the focus event will occur before the click event, so we need to stop the propagation of the
	// focus event to prevent it from firing a click event as well.  Therefore if we do see a click event, we know
	// that the user has clicked in the element when we already had focus - that is, the general comment entry was
	// cancelled and put us back here.
	const handleFocus = useCallback(
		(event: React.FocusEvent) => {
			// Prevent the focus event from further processing and firing a click event
			event.stopPropagation();
			onFocus();
		},
		[onFocus],
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={commentAddPromptContainerStyle}
			onClick={onClick}
			// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
			{...(fg('confluence_frontend_comments_a11y_kb')
				? {
						onBlur,
						onFocus: handleFocus,
						onKeyDown: handleKeydown,
						tabIndex: 0,
						id: 'comments-panel-add-comment-button',
						role: 'button',
						'aria-label': ariaLabel,
					}
				: {})}
			ref={buttonRef}
			data-testId="add-comment-button"
		>
			<Inline xcss={commentAddPromptMessage}>
				{formatMessage(i18n.commentPanelFooterPlaceholder)}
			</Inline>
		</Box>
	);
};
